import { getDatabase, ref, get, set } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { ComponentLifetime, TemplatesByType } from '../../TemplatesEditor/types';
import { Building } from '../../../../types/protocols';
import { MaintenanceDraftWithDone } from '../types';
import { formatDateForStorage } from '../../../../Helpers/DateUtils';
import { ValueStatus } from '../../../../types/protocols';
import { recordActivity } from '../../../../api/activityApi';

const db = getDatabase();

/**
 * Load building data from Firebase
 */
export const loadBuilding = async (buildingKey: string): Promise<Building | null> => {
    try {
        const [summarySnapshot, modelSnapshot] = await Promise.all([
            get(ref(db, `buildingsSummary/${buildingKey}`)),
            get(ref(db, `buildings/${buildingKey}`))
        ]);

        if (summarySnapshot.exists() || modelSnapshot.exists()) {
            const building: Building = {
                key: buildingKey,
                summary: summarySnapshot.exists() ? summarySnapshot.val() : { key: buildingKey },
                model: modelSnapshot.exists() ? modelSnapshot.val() : { key: buildingKey }
            };
            return building;
        }
        return null;
    } catch (error) {
        console.error('Error loading building:', error);
        return null;
    }
};

/**
 * Load component lifetimes from Firebase
 * @deprecated Use useBuildComponentTypes().getLifetime() instead
 */
export const loadComponentLifetimes = async (): Promise<Record<string, ComponentLifetime>> => {
    console.warn('loadComponentLifetimes is deprecated. Use useBuildComponentTypes().getLifetime() instead.');
    return {};
};

/**
 * Load maintenance templates from Firebase
 * @deprecated Use useBuildComponentTypes().getTemplates() instead
 */
export const loadMaintenanceTemplates = async (): Promise<TemplatesByType> => {
    console.warn('loadMaintenanceTemplates is deprecated. Use useBuildComponentTypes().getTemplates() instead.');
    return {};
};

/**
 * Load maintenance plan draft from Firebase
 */
export const loadMaintenancePlanDraft = async (buildingKey: string): Promise<MaintenanceDraftWithDone | null> => {
    try {
        const draftRef = ref(db, `maintenance/drafts/${buildingKey}`);
        const snapshot = await get(draftRef);
        
        if (snapshot.exists()) {
            const draftData = snapshot.val();
            
            const loadedDraft: MaintenanceDraftWithDone = {
                components: draftData.components || {},
                metadata: {
                    savedAt: draftData.metadata?.savedAt || formatDateForStorage(new Date()),
                    buildingKey: draftData.metadata?.buildingKey || buildingKey,
                    createdBy: draftData.metadata?.createdBy
                },
                // Preserve rejection comment if it exists
                ...(draftData.rejectionComment && { rejectionComment: draftData.rejectionComment }),
            };
            return loadedDraft;
        }
        return null;
    } catch (error) {
        console.error('Error loading maintenance plan draft:', error);
        return null;
    }
};

/**
 * Save maintenance plan draft to Firebase
 */
export const saveDraft = async (
    buildingKey: string,
    draft: MaintenanceDraftWithDone
): Promise<void> => {
    try {
        const auth = getAuth();
        const user = auth.currentUser;
        
        // Update metadata
        const updatedDraft: MaintenanceDraftWithDone = {
            ...draft,
            metadata: {
                ...draft.metadata,
                savedAt: formatDateForStorage(new Date()),
                buildingKey,
                createdBy: user?.uid || draft.metadata?.createdBy || '',
                version: draft.metadata?.version || '1.0',
                priceYear: draft.metadata?.priceYear || new Date().getFullYear()
            }
        };

        // Save to Firebase
        const draftRef = ref(db, `maintenance/drafts/${buildingKey}`);
        await set(draftRef, updatedDraft);
        recordActivity('maintenancePlan.updated', { buildingKey, targetKey: buildingKey });
    } catch (error) {
        console.error('Error saving draft:', error);
        throw error;
    }
};

/**
 * Delete maintenance plan draft from Firebase
 */
export const deleteDraft = async (buildingKey: string): Promise<void> => {
    try {
        await set(ref(db, `maintenance/drafts/${buildingKey}`), null);
        recordActivity('maintenancePlan.deleted', { buildingKey, targetKey: buildingKey });
    } catch (error) {
        console.error('Error deleting draft:', error);
        throw error;
    }
};

/**
 * Save condition assessment for a component
 */
export const saveCondition = async (
    buildingKey: string,
    componentKey: string,
    conditionValue: number,
    comment?: string
): Promise<void> => {
    try {
        const auth = getAuth();
        const conditionData = {
            value: {
                value: conditionValue,
                ...(comment?.trim() && { comment: comment.trim() })
            },
            updatedWhen: formatDateForStorage(new Date()),
            updatedBy: auth.currentUser?.uid || '',
            status: ValueStatus.set,
            assessedAt: formatDateForStorage(new Date())
        };
        
        // Save to Firebase
        await set(ref(db, `buildings/${buildingKey}/structure/components/${componentKey}/condition`), conditionData);
        recordActivity('component.updated', { buildingKey, targetKey: componentKey });
    } catch (error) {
        console.error('Error saving condition:', error);
        throw error;
    }
};

